import api from './api';

export interface TeacherSpecialty {
  id: number;
  specialty: number;
  specialty_name: string;
  specialty_full_path: string;
  start_date?: string;
  end_date?: string;
  is_primary: boolean;
  created_at: string;
}

export interface Teacher {
  id: number;
  first_name: string;
  last_name: string;
  father_name: string;
  full_name: string;
  fin_code: string;
  id_number: string;
  work_percentage: number;
  salary: number;
  phone?: string;
  email?: string;
  company: number;
  is_active: boolean;
  course_count: number;
  specialties_detail: TeacherSpecialty[];
  active_courses: any[];
  exam_panel_access?: boolean;
  exam_username?: string;
  created_at: string;
  updated_at: string;
}

export interface TeacherList {
  id: number;
  full_name: string;
  fin_code: string;
  id_number: string;
  work_percentage: number;
  salary: number;
  phone?: string;
  email?: string;
  company_name: string;
  course_count: number;
  courses_names: string[];
  is_active: boolean;
  created_at: string;
}

export interface TeacherCreate {
  first_name: string;
  last_name: string;
  father_name: string;
  fin_code: string;
  id_number: string;
  work_percentage: number;
  salary: number;
  phone?: string;
  email?: string;
  company: number;
  is_active: boolean;
  specialties: number[];
  // İmtahan paneli hesabı
  exam_panel_access?: boolean;
  exam_username?: string;
  exam_password?: string;
}

export interface TeacherFilters {
  search?: string;
  company?: number;
  is_active?: boolean;
  work_percentage?: number;
  course?: number;
}

export interface TeacherStatistics {
  total_teachers: number;
  active_teachers: number;
  inactive_teachers: number;
  by_work_percentage: Record<string, number>;
  by_company: Array<{ company__name: string; count: number }>;
  average_salary: number;
  top_courses: Array<{ name: string; teacher_count: number }>;
}

export interface TeacherResponse {
  success: boolean;
  data?: TeacherList[];
  message?: string;
}

export interface TeacherDetailResponse {
  success: boolean;
  data?: Teacher;
  message?: string;
  errors?: any;
}

export interface TeacherStatisticsResponse {
  success: boolean;
  data?: TeacherStatistics;
  message?: string;
}

export const teacherAPI = {
  getTeachers: async (filters?: TeacherFilters): Promise<TeacherResponse> => {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.company) params.append('company', filters.company.toString());
      if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
      if (filters?.work_percentage) params.append('work_percentage', filters.work_percentage.toString());
      if (filters?.course) params.append('course', filters.course.toString());

      const response = await api.get(`/teachers/?${params.toString()}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Müəllimlər yüklənmədi'
      };
    }
  },

  getTeacher: async (id: number): Promise<TeacherDetailResponse> => {
    try {
      const response = await api.get(`/teachers/${id}/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Müəllim məlumatları yüklənmədi'
      };
    }
  },

  createTeacher: async (data: TeacherCreate): Promise<TeacherDetailResponse> => {
    try {
      const response = await api.post('/teachers/', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Müəllim yaradılmadı',
        errors: error.response?.data?.errors
      };
    }
  },

  updateTeacher: async (id: number, data: Partial<TeacherCreate>): Promise<TeacherDetailResponse> => {
    try {
      const response = await api.put(`/teachers/${id}/`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Müəllim yenilənmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  deleteTeacher: async (id: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.delete(`/teachers/${id}/`);
      // 204 No Content status code üçün response.data boş ola bilər
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: response.data?.message || 'Müəllim silindi'
        };
      }
      return {
        success: true,
        message: response.data.message || 'Müəllim silindi'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Müəllim silinmədi'
      };
    }
  },

  assignCourse: async (teacherId: number, courseId: number, isPrimary: boolean = false): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post(`/teachers/${teacherId}/assign_course/`, {
        course_id: courseId,
        is_primary: isPrimary
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Kurs təyin edilmədi'
      };
    }
  },

  removeCourse: async (teacherId: number, courseId: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post(`/teachers/${teacherId}/remove_course/`, {
        course_id: courseId
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Kurs çıxarılmadı'
      };
    }
  },

  getTeachersByCourse: async (courseId: number): Promise<TeacherResponse> => {
    try {
      const response = await api.get(`/teachers/by_course/?course_id=${courseId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Müəllimlər yüklənmədi'
      };
    }
  },

  getStatistics: async (companyId?: number): Promise<TeacherStatisticsResponse> => {
    try {
      const params = companyId ? `?company=${companyId}` : '';
      const response = await api.get(`/teachers/statistics/${params}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Statistikalar yüklənmədi'
      };
    }
  },

  checkFinCodeExists: async (finCode: string, companyId: number, excludeTeacherId?: number): Promise<{ exists: boolean; teacherName?: string }> => {
    try {
      const params = new URLSearchParams();
      params.append('fin_code', finCode);
      params.append('company', companyId.toString());
      if (excludeTeacherId) {
        params.append('exclude_id', excludeTeacherId.toString());
      }

      const response = await api.get(`/teachers/check_fin_code/?${params.toString()}`);
      return {
        exists: response.data.exists,
        teacherName: response.data.teacher_name
      };
    } catch (error: any) {
      console.error('FIN kodu yoxlanılarkən xəta:', error);
      return { exists: false };
    }
  },

  checkIdNumberExists: async (idNumber: string, companyId: number, excludeTeacherId?: number): Promise<{ exists: boolean; teacherName?: string }> => {
    try {
      const params = new URLSearchParams();
      params.append('id_number', idNumber);
      params.append('company', companyId.toString());
      if (excludeTeacherId) {
        params.append('exclude_id', excludeTeacherId.toString());
      }

      const response = await api.get(`/teachers/check_id_number/?${params.toString()}`);
      return {
        exists: response.data.exists,
        teacherName: response.data.teacher_name
      };
    } catch (error: any) {
      console.error('ID nömrəsi yoxlanılarkən xəta:', error);
      return { exists: false };
    }
  },
};